import React, { useEffect, useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { DashboardCard } from '../components/dashboard/DashboardCard';
import { CardBrandsTable } from '../components/dashboard/CardBrandsTable';
import { CardBrandsChart } from '../components/dashboard/CardBrandsChart';
import { KpiCard } from '../components/dashboard/KpiCard';
import { TabView } from '../components/dashboard/TabView';
import { GeoPaymentMap } from '../components/dashboard/GeoPaymentMap';
import { CardErrorCodes } from '../components/dashboard/CardErrorCodes';
import { CompanySelector, Company } from '../components/dashboard/CompanySelector';
import { CreditCardIcon, ArrowUpRightIcon, ArrowDownRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, BuildingIcon, FilterIcon, RotateCcwIcon, ClockIcon as PendingIcon, AlertCircleIcon, ReceiptIcon, RefreshCcwIcon, X } from 'lucide-react';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData } from '../components/dashboard/DataGenerator';
export function Overview() {
  const [timeRange, setTimeRange] = useState('7d');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [paymentVolumeData, setPaymentVolumeData] = useState([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState([]);
  const [transactionCountData, setTransactionCountData] = useState([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState([]);
  const [averageTransactionData, setAverageTransactionData] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [attemptsData, setAttemptsData] = useState([]);
  const [geoPaymentData, setGeoPaymentData] = useState([]);
  const [refundData, setRefundData] = useState([]);
  const [previousRefundData, setPreviousRefundData] = useState([]);
  const [pendingRefundsData, setPendingRefundsData] = useState([]);
  const [refundReasonsData, setRefundReasonsData] = useState([]);
  // Load companies on component mount
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    // Default to all companies selected
    setSelectedCompanies(companiesData);
  }, []);
  // Update data when time range or selected companies change
  useEffect(() => {
    // Get raw data
    const rawPaymentVolumeData = generatePaymentVolumeData(timeRange);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, timeRange);
    const rawTransactionCountData = generateTransactionCountData(timeRange);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, timeRange);
    const rawAverageTransactionData = generateAverageTransactionData(timeRange);
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawConversionData = generateConversionData();
    const rawAttemptsData = generateAttemptsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData(timeRange);
    const rawPreviousRefundData = generatePreviousRefundData(rawRefundData, timeRange);
    const rawPendingRefundsData = generatePendingRefundsData();
    const rawRefundReasonsData = generateRefundReasonsData();
    // Filter data by selected companies if needed
    const selectedCompanyIds = selectedCompanies.map(company => company.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length;
    if (isAllCompanies || selectedCompanyIds.length === 0) {
      // Use all data
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setConversionData(rawConversionData);
      setAttemptsData(rawAttemptsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setPreviousRefundData(rawPreviousRefundData);
      setPendingRefundsData(rawPendingRefundsData);
      setRefundReasonsData(rawRefundReasonsData);
    } else {
      // Filter data based on selected companies
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData); // Keep the same proportions
      setConversionData(rawConversionData); // Keep the same proportions
      setAttemptsData(filterDataByCompanies(rawAttemptsData, selectedCompanyIds));
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setPreviousRefundData(filterDataByCompanies(rawPreviousRefundData, selectedCompanyIds));
      setPendingRefundsData(filterDataByCompanies(rawPendingRefundsData, selectedCompanyIds));
      setRefundReasonsData(rawRefundReasonsData); // Keep the same proportions
    }
  }, [timeRange, selectedCompanies, companies]);
  // Calculate total payment volume
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  // Calculate total transactions
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  // Calculate average transaction
  const avgTransaction = totalVolume / totalTransactions;
  // Calculate conversion rate
  const conversionRate = conversionData.length > 2 ? conversionData[2].value / conversionData[0].value * 100 : 0;
  // Calculate percentage of transactions by single attempt
  const singleAttemptRate = attemptsData.length > 0 ? attemptsData[0].count / attemptsData.reduce((sum, item) => sum + item.count, 0) * 100 : 0;
  // Calculate average number of attempts
  const avgAttempts = attemptsData.length > 0 ? attemptsData.reduce((sum, item, index) => {
    const attemptNumber = index === 3 ? 4 : index + 1; // 4+ is counted as 4
    return sum + item.count * attemptNumber;
  }, 0) / attemptsData.reduce((sum, item) => sum + item.count, 0) : 0;
  // Calculate refund metrics
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const totalRefundCount = refundData.reduce((sum, item) => sum + item.count, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  // Format dates based on time range
  const formatDate = (date, timeRange) => {
    if (timeRange === 'today') {
      // For today's data, format as hour
      if (typeof date === 'string' && date.includes('T')) {
        const dateObj = new Date(date);
        return dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      // If we have an hour field, use that
      return date;
    } else if (timeRange === '7d' || timeRange === '30d') {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } else {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    }
  };
  // Get the title for the time period based on selected range
  const getTimeRangeTitle = () => {
    switch (timeRange) {
      case 'today':
        return 'Today (hourly)';
      case '7d':
        return 'Last 7 days';
      case '30d':
        return 'Last 30 days';
      case 'year':
        return 'This year';
      case 'all':
        return 'All time';
      default:
        return 'Selected period';
    }
  };
  // Get the x-axis key for the data
  const getXAxisKey = () => {
    return timeRange === 'today' ? 'hour' : 'date';
  };
  // Table view for payment volume data
  const PaymentVolumeTable = () => <div className="overflow-x-auto" data-id="element-1707">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1708">
        <thead className="bg-gray-50" data-id="element-1709">
          <tr data-id="element-1710">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1711">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1712">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1713">
          {paymentVolumeData.map((row, index) => <tr key={index} data-id="element-1714">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1715">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1716">
                ${row.amount.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for transaction count data
  const TransactionCountTable = () => <div className="overflow-x-auto" data-id="element-1717">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1718">
        <thead className="bg-gray-50" data-id="element-1719">
          <tr data-id="element-1720">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1721">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1722">
              Transactions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1723">
          {transactionCountData.map((row, index) => <tr key={index} data-id="element-1724">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1725">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1726">
                {row.count.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for payment methods data
  const PaymentMethodsTable = () => <div className="overflow-x-auto" data-id="element-1727">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1728">
        <thead className="bg-gray-50" data-id="element-1729">
          <tr data-id="element-1730">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1731">
              Payment Method
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1732">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1733">
          {paymentMethodsData.map((row, index) => <tr key={index} data-id="element-1734">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1735">
                {row.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1736">
                {row.value}%
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for refund data
  const RefundDataTable = () => <div className="overflow-x-auto" data-id="element-1737">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1738">
        <thead className="bg-gray-50" data-id="element-1739">
          <tr data-id="element-1740">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1741">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1742">
              Refund Amount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1743">
              Count
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1744">
          {refundData.map((row, index) => <tr key={index} data-id="element-1745">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1746">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1747">
                ${row.amount.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1748">
                {row.count}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for geographic payment data
  const GeoPaymentTable = () => <div className="overflow-x-auto" data-id="element-1749">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1750">
        <thead className="bg-gray-50" data-id="element-1751">
          <tr data-id="element-1752">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1753">
              Country
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1754">
              Transactions
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1755">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1756">
          {geoPaymentData.map((row, index) => <tr key={index} data-id="element-1757">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1758">
                {row.country}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1759">
                {row.transactions.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1760">
                ${row.amount.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Determine if we're showing aggregated data
  const isAggregated = selectedCompanies.length > 1 && selectedCompanies.length < companies.length;
  return <div className="space-y-6" data-id="element-1761">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-1762">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-1763">
          Dashboard Overview
        </h1>
        <div className="flex flex-col gap-3 w-full md:w-auto" data-id="element-1764">
          {/* Controls row - Time Range Selector first, then Company Selector */}
          <div className="flex flex-wrap items-center gap-3 justify-end" data-id="element-1765">
            {/* Time Range Selector */}
            <TimeSelector value={timeRange} onChange={setTimeRange} data-id="element-1766" />
            {/* Company Selector */}
            {companies.length > 0 && <CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-1767" />}
          </div>
          {/* Selected company tags in a separate row */}
          {selectedCompanies.length > 1 && selectedCompanies.length < companies.length && <div className="flex flex-wrap gap-2 mt-2 justify-end" data-id="element-1768">
                {selectedCompanies.slice(0, 3).map(company => <div key={company.id} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full" data-id="element-1769">
                    {company.name}
                    <button onClick={() => {
              setSelectedCompanies(selectedCompanies.filter(c => c.id !== company.id));
            }} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-1770">
                      <X size={12} data-id="element-1771" />
                    </button>
                  </div>)}
                {selectedCompanies.length > 3 && <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-1772">
                    +{selectedCompanies.length - 3} more
                  </div>}
              </div>}
        </div>
      </div>
      {/* Aggregation indicator */}
      {isAggregated && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start" data-id="element-1773">
          <FilterIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1774" />
          <div data-id="element-1775">
            <p className="text-sm text-blue-800" data-id="element-1776">
              <span className="font-medium" data-id="element-1777">Aggregated view:</span> Showing
              combined data for {selectedCompanies.length} companies.
            </p>
            <div className="mt-1 flex flex-wrap gap-1" data-id="element-1778">
              {selectedCompanies.map(company => <span key={company.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1779">
                  {company.name}
                </span>)}
            </div>
          </div>
        </div>}
      {/* First row: Only 4 KPIs in a horizontal row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1780">
        <KpiCard title="Total Volume" value={`$${(totalVolume / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-1782" />} trend={{
        value: '8.2%',
        isPositive: true
      }} color="blue" data-id="element-1781" />
        <KpiCard title="Transactions" value={totalTransactions.toLocaleString()} icon={<BarChart3Icon size={18} data-id="element-1784" />} trend={{
        value: '5.7%',
        isPositive: true
      }} color="green" data-id="element-1783" />
        <KpiCard title="Avg Transaction" value={`$${avgTransaction.toFixed(2)}`} icon={<ArrowUpRightIcon size={18} data-id="element-1786" />} trend={{
        value: '2.3%',
        isPositive: true
      }} color="purple" data-id="element-1785" />
        <KpiCard title="Conversion" value={`${conversionRate.toFixed(1)}%`} icon={<CreditCardIcon size={18} data-id="element-1788" />} trend={{
        value: '1.4%',
        isPositive: false
      }} color="orange" data-id="element-1787" />
      </div>
      {/* Second row: 3-column chart and 1-column stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1789">
        {/* Payment Volume Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col h-full" data-id="element-1790">
          <DashboardCard title="Payment Volume" subtitle={getTimeRangeTitle()} className="h-full flex-grow" data-id="element-1791">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-1793">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1794">
                        <LineChart data={[...paymentVolumeData, ...previousPaymentVolumeData]} data-id="element-1795">
                          <CartesianGrid strokeDasharray="3 3" data-id="element-1796" />
                          <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-1797" />
                          <YAxis tickFormatter={value => `$${value / 1000}k`} data-id="element-1798" />
                          <Tooltip formatter={value => [`$${value.toLocaleString()}`, 'Volume']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-1799" />
                          <Legend data-id="element-1800" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={2} name="Current Period" connectNulls dot={{
                    r: 2
                  }} activeDot={{
                    r: 6
                  }} data={paymentVolumeData} data-id="element-1801" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousPaymentVolumeData} data-id="element-1802" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
          }, {
            label: 'Data',
            content: <PaymentVolumeTable data-id="element-1803" />
          }]} data-id="element-1792" />
          </DashboardCard>
        </div>
        {/* Stacked KPIs (1 column) */}
        <div className="flex flex-col h-full" data-id="element-1804">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-1805">
            <KpiCard title="Daily Average" value={`$${(totalVolume / paymentVolumeData.length / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-1807" />} trend={{
            value: '4.5%',
            isPositive: true
          }} color="blue" data-id="element-1806" />
            <KpiCard title="Monthly Growth" value="12.7%" icon={<ArrowUpRightIcon size={18} data-id="element-1809" />} trend={{
            value: '3.2%',
            isPositive: true
          }} color="green" data-id="element-1808" />
            <KpiCard title="YoY Growth" value="24.3%" icon={<BarChart3Icon size={18} data-id="element-1811" />} trend={{
            value: '5.8%',
            isPositive: true
          }} color="purple" data-id="element-1810" />
            <KpiCard title="Transaction Growth" value="18.5%" icon={<ArrowUpRightIcon size={18} data-id="element-1813" />} trend={{
            value: '2.7%',
            isPositive: true
          }} color="orange" data-id="element-1812" />
          </div>
        </div>
      </div>
      {/* Third row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1814">
        {/* Transaction Count Chart (2 columns) */}
        <DashboardCard title="Number of Transactions" subtitle={getTimeRangeTitle()} data-id="element-1815">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-64" data-id="element-1817">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1818">
                      <LineChart data={[...transactionCountData, ...previousTransactionCountData]} data-id="element-1819">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-1820" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-1821" />
                        <YAxis data-id="element-1822" />
                        <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-1823" />
                        <Legend data-id="element-1824" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={2} name="Current Period" connectNulls dot={{
                  r: 2
                }} activeDot={{
                  r: 6
                }} data={transactionCountData} data-id="element-1825" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousTransactionCountData} data-id="element-1826" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <TransactionCountTable data-id="element-1827" />
        }]} data-id="element-1816" />
        </DashboardCard>
        {/* Average Transaction Chart (2 columns) */}
        <DashboardCard title="Average Transaction" subtitle={`Last ${timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : 'year'}`} data-id="element-1828">
          <div className="h-64" data-id="element-1829">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1830">
              <BarChart data={averageTransactionData} data-id="element-1831">
                <CartesianGrid strokeDasharray="3 3" data-id="element-1832" />
                <XAxis dataKey="date" tickFormatter={date => formatDate(date, timeRange)} data-id="element-1833" />
                <YAxis tickFormatter={value => `$${value}`} data-id="element-1834" />
                <Tooltip formatter={value => [`$${value.toLocaleString()}`, 'Average']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-1835" />
                <Bar dataKey="amount" fill="#7FC29B" data-id="element-1836" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Fourth row: Card Brands chart and Success Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1837">
        {/* Card Brands Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col" data-id="element-1838">
          <DashboardCard title="Card Brands" subtitle="Transaction Volume" className="h-full flex-grow" data-id="element-1839">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-1841">
                      <CardBrandsChart data-id="element-1842" />
                    </div>
          }, {
            label: 'Data',
            content: <CardBrandsTable data-id="element-1843" />
          }]} data-id="element-1840" />
          </DashboardCard>
        </div>
        {/* Card Success Rate (1 column) */}
        <div className="flex flex-col" data-id="element-1844">
          <DashboardCard title="Card Success Rate" subtitle="By Brand" className="h-full flex-grow" data-id="element-1845">
            <div className="space-y-3 p-2" data-id="element-1846">
              <div className="flex justify-between items-center" data-id="element-1847">
                <div className="flex items-center" data-id="element-1848">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/visa.svg" alt="Visa" className="h-6 w-10 mr-2" data-id="element-1849" />
                  <span data-id="element-1850">Visa</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-1851">98.2%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1852">
                <div className="flex items-center" data-id="element-1853">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/mastercard.svg" alt="MasterCard" className="h-6 w-10 mr-2" data-id="element-1854" />
                  <span data-id="element-1855">MasterCard</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-1856">97.5%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1857">
                <div className="flex items-center" data-id="element-1858">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/amex.svg" alt="Amex" className="h-6 w-10 mr-2" data-id="element-1859" />
                  <span data-id="element-1860">Amex</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-1861">95.8%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1862">
                <div className="flex items-center" data-id="element-1863">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/discover.svg" alt="Discover" className="h-6 w-10 mr-2" data-id="element-1864" />
                  <span data-id="element-1865">Discover</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-1866">96.3%</span>
              </div>
            </div>
            <CardErrorCodes timeRange={timeRange} data-id="element-1867" />
          </DashboardCard>
        </div>
      </div>
      {/* New fifth row: Card Volume KPIs in horizontal layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1868">
        <KpiCard title="Visa Volume" value="$112.5k" icon={<CreditCardIcon size={18} data-id="element-1870" />} color="blue" data-id="element-1869" />
        <KpiCard title="MC Volume" value="$87.3k" icon={<CreditCardIcon size={18} data-id="element-1872" />} color="orange" data-id="element-1871" />
        <KpiCard title="Amex Volume" value="$42.8k" icon={<CreditCardIcon size={18} data-id="element-1874" />} color="green" data-id="element-1873" />
        <KpiCard title="Discover Volume" value="$21.4k" icon={<CreditCardIcon size={18} data-id="element-1876" />} color="purple" data-id="element-1875" />
      </div>
      {/* Fifth row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1877">
        {/* Payment Methods Chart (2 columns) */}
        <DashboardCard title="Payment Methods" subtitle="Transaction Count" data-id="element-1878">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-1880">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1881">
                      <PieChart data-id="element-1882">
                        <Pie data={paymentMethodsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} fill="#8884d8" dataKey="value" label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-1883">
                          {paymentMethodsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1884" />)}
                        </Pie>
                        <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-1885" />
                        <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-1886" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <PaymentMethodsTable data-id="element-1887" />
        }]} data-id="element-1879" />
        </DashboardCard>
        {/* Payment Conversion Chart (2 columns) */}
        <DashboardCard title="Payment Conversion" subtitle="Success Rate" data-id="element-1888">
          <div className="h-64" data-id="element-1889">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1890">
              <BarChart data={conversionData} data-id="element-1891">
                <CartesianGrid strokeDasharray="3 3" data-id="element-1892" />
                <XAxis dataKey="name" data-id="element-1893" />
                <YAxis data-id="element-1894" />
                <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} data-id="element-1895" />
                <Bar dataKey="value" fill="#3498db" data-id="element-1896">
                  {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1897" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-center text-sm text-gray-600" data-id="element-1898">
            Conversion rate: {conversionRate.toFixed(1)}%
          </div>
        </DashboardCard>
      </div>
      {/* Sixth row: One 2-column chart and 4 vertically stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1899">
        {/* Attempts Per Payment Chart (2 columns) */}
        <div className="flex flex-col h-full" data-id="element-1900">
          <DashboardCard title="Attempts Per Payment" subtitle="Before Success" className="h-full flex-grow" data-id="element-1901">
            <div className="h-80" data-id="element-1902">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1903">
                <BarChart data={attemptsData} data-id="element-1904">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-1905" />
                  <XAxis dataKey="attempts" data-id="element-1906" />
                  <YAxis data-id="element-1907" />
                  <Tooltip formatter={value => [value.toLocaleString(), 'Payments']} data-id="element-1908" />
                  <Bar dataKey="count" fill="#5DAFBA" data-id="element-1909" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center text-sm text-gray-600" data-id="element-1910">
              Average attempts: {avgAttempts.toFixed(1)}
            </div>
          </DashboardCard>
        </div>
        {/* 4 Vertically stacked KPIs */}
        <div className="flex flex-col h-full" data-id="element-1911">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-1912">
            <KpiCard title="First Attempt" value={`${singleAttemptRate.toFixed(1)}%`} icon={<CheckCircleIcon size={18} data-id="element-1914" />} trend={{
            value: '3.1%',
            isPositive: true
          }} color="green" data-id="element-1913" />
            <KpiCard title="Avg Attempts" value={avgAttempts.toFixed(1)} icon={<ClockIcon size={18} data-id="element-1916" />} trend={{
            value: '0.2',
            isPositive: false
          }} color="blue" data-id="element-1915" />
            <KpiCard title="Customers" value="1,243" icon={<UsersIcon size={18} data-id="element-1918" />} trend={{
            value: '12.5%',
            isPositive: true
          }} color="purple" data-id="element-1917" />
            <KpiCard title="Chargeback" value="0.3%" icon={<PercentIcon size={18} data-id="element-1920" />} trend={{
            value: '0.1%',
            isPositive: true
          }} color="orange" data-id="element-1919" />
          </div>
        </div>
      </div>
      {/* New Refunds Row: Header */}
      <div className="mt-8 mb-4" data-id="element-1921">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center" data-id="element-1922">
          <RotateCcwIcon size={20} className="mr-2 text-red-500" data-id="element-1923" />
          Refund Analysis
        </h2>
      </div>
      {/* Refunds KPIs row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1924">
        <KpiCard title="Total Refunds" value={`$${(totalRefundAmount / 1000).toFixed(1)}k`} icon={<RotateCcwIcon size={18} data-id="element-1926" />} trend={{
        value: '2.1%',
        isPositive: false
      }} color="red" data-id="element-1925" />
        <KpiCard title="Refund Count" value={totalRefundCount} icon={<ReceiptIcon size={18} data-id="element-1928" />} trend={{
        value: '1.5%',
        isPositive: false
      }} color="red" data-id="element-1927" />
        <KpiCard title="Refund Rate" value={`${refundPercentage.toFixed(1)}%`} icon={<PercentIcon size={18} data-id="element-1930" />} trend={{
        value: '0.2%',
        isPositive: true
      }} color="blue" data-id="element-1929" />
        <KpiCard title="Avg Refund" value={`$${(totalRefundAmount / totalRefundCount).toFixed(2)}`} icon={<DollarSignIcon size={18} data-id="element-1932" />} trend={{
        value: '3.8%',
        isPositive: false
      }} color="orange" data-id="element-1931" />
      </div>
      {/* Refunds charts row - Modified to remove Pending Refunds */}
      <div className="grid grid-cols-1 gap-6" data-id="element-1933">
        {/* Refund Amounts Chart (full width) */}
        <DashboardCard title="Refund Volume" subtitle={getTimeRangeTitle()} icon={<RotateCcwIcon size={18} className="text-red-500 mr-2" data-id="element-1935" />} className="h-full flex-grow" data-id="element-1934">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-1937">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1938">
                      <LineChart data={[...refundData, ...previousRefundData]} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-1939">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-1940" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-1941" />
                        <YAxis tickFormatter={value => `$${value}`} data-id="element-1942" />
                        <Tooltip formatter={value => [`$${value.toLocaleString()}`, 'Refund Amount']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-1943" />
                        <Legend data-id="element-1944" />
                        <Line type="monotone" dataKey="amount" name="Current Period" stroke="#ef4444" strokeWidth={2} activeDot={{
                  r: 6
                }} connectNulls dot={{
                  r: 2
                }} data={refundData} data-id="element-1945" />
                        <Line type="monotone" dataKey="amount" name="Previous Period" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="5 5" connectNulls dot={false} data={previousRefundData} data-id="element-1946" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <RefundDataTable data-id="element-1947" />
        }]} data-id="element-1936" />
        </DashboardCard>
      </div>
      {/* Refund Reasons */}
      <div data-id="element-1948">
        <DashboardCard title="Refund Reasons" subtitle="Distribution by Category" icon={<RefreshCcwIcon size={18} className="text-blue-500 mr-2" data-id="element-1950" />} data-id="element-1949">
          <div className="h-80" data-id="element-1951">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1952">
              <PieChart data-id="element-1953">
                <Pie data={refundReasonsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} dataKey="count" nameKey="reason" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-1954">
                  {refundReasonsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1955" />)}
                </Pie>
                <Tooltip formatter={value => [value, 'Count']} data-id="element-1956" />
                <Legend data-id="element-1957" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Seventh row: Geographic payment map */}
      <div data-id="element-1958">
        <DashboardCard title="Payment Geography" subtitle="Transaction Origin" icon={<GlobeIcon size={18} className="text-blue-600 mr-2" data-id="element-1960" />} data-id="element-1959">
          <TabView tabs={[{
          label: 'Map',
          content: <div className="h-96" data-id="element-1962">
                    <GeoPaymentMap data={geoPaymentData} data-id="element-1963" />
                  </div>
        }, {
          label: 'Data',
          content: <GeoPaymentTable data-id="element-1964" />
        }]} data-id="element-1961" />
        </DashboardCard>
      </div>
    </div>;
}